/* eslint-disable max-lines */
import axios from 'axios';
import { trackPromise } from 'react-promise-tracker';
import { WORKFLOW } from '../utils/constants';
import {
  OpenReject,
  GroupForInfo,
  OpenConvalidaResponse,
  WfInfoResponse,
  OpenRejectToPrevious,
  OpenPark,
  OpenInfoFromWorkflow,
  OpenConvalidaInfoResponse,
  LitigationCausalResponse,
  GetWfHistoryResponse,
  SimpleError,
  OpenCloseLitigationResponse,
  ApproveWfTaskReponse,
  WfCausalNameReject,
  WfDefinitionEngine,
} from 'models/response';
import {
  CloseParkDocument,
  CloseReject,
  WfInfoBody,
  OpenWfBody,
  OpenWfInfoBody,
  CloseRejectPrevious,
  CloseConvalidateDocumentBody,
  CloseConvalidateInfoDocumentBody,
  OpenLitigationBody,
  CloseLitigationBody,
  PrintPdfLitigationRequest,
} from '../models/request';

/**
 * This API open the document park process
 * @param {number} protocol
 * @return {Promise<AxiosResponse<OpenPark>>}
 */
const openParkDocument = (protocol: number) =>
  trackPromise(axios.post<OpenPark>(`${WORKFLOW}/openParkDocument?protocol=${protocol}`), 'modal');

/**
 * This API close the document park process
 * @param {Object} body
 * @param {number} body.protocol
 * @param {string} body.note
 * @return {Promise<AxiosResponse<string>>}
 */
const closeParkDocument = (body: CloseParkDocument) =>
  trackPromise(axios.post<string>(`${WORKFLOW}/closeParkDocument`, body), 'modal');

/**
 * Remove document from parked
 * @param {Object} body
 * @param {number} body.protocol
 * @param {string} body.note
 * @return {Promise<AxiosResponse<string>>}
 */
const closeCloseParkDocument = (body: CloseParkDocument) =>
  trackPromise(axios.post<string>(`${WORKFLOW}/closeCloseParkDocument`, body), 'modal');

/**
 * This API open the document reject process
 * @param {number} protocol
 * @return {Promise<AxiosResponse<OpenReject>>}
 */
const openReject = (protocol: number) =>
  trackPromise(axios.post<OpenReject>(`${WORKFLOW}/openRejectDocument?protocol=${protocol}`), 'modal');

/**
 * This API close the document reject process
 * @param {Object} body
 * @param {number} body.protocol
 * @param {string} body.note
 * @param {number} body.idWfCausal
 * @return {Promise<AxiosResponse<string>>}
 */
const closeReject = (body: CloseReject) =>
  trackPromise(axios.post<string>(`${WORKFLOW}/closeRejectDocument`, { ...body }), 'modal');

/**
 * This API to get send to WF info
 * @param {Object} body
 * @param {number} body.idCompany
 * @param {number} body.idUser
 * @return {Promise<AxiosResponse<WfInfoResponse>>}
 */
const getWfInfoForSuspend = (body: WfInfoBody) =>
  trackPromise(axios.post<WfInfoResponse>(`${WORKFLOW}/getWfInfoForSuspend`, body), 'modal');

/**
 * This API to get send to WF info for info flow
 * @param {Object} body
 * @param {number} body.idCompany
 * @return {Promise<AxiosResponse<GroupForInfo[]>>}
 */
const getGroupsForInfo = (body: { idCompany: number }) =>
  trackPromise(axios.post<GroupForInfo[]>(`${WORKFLOW}/getGroupsForInfo`, body), 'modal');

/**
 * This API is used to send documents to WF
 * @param {Object} body
 * @param {number} body.idCausal
 * @param {number} body.idWorkflowDefinition
 * @param {number[]} body.protocols
 * @param {number} body.idCompany
 * @param {number} body.idTaskDefinition
 * @param {number} body.idGroups
 * @param {string} body.note
 * @return {Promise<AxiosResponse>}
 */
const openWorkFlow = (body: OpenWfBody) => trackPromise(axios.post(`${WORKFLOW}/openWorkflow`, body), 'modal');

/**
 * This API is used to send documents to WF for info flow
 * @param {Object} body
 * @param {number[]} body.protocols
 * @param {number} body.idUser
 * @param {number} body.idGroup
 * @param {number} body.idWorkflowDefinition
 * @param {number} body.idCompany
 * @param {string} body.note
 * @return {Promise<AxiosResponse>}
 */
const openWfInfo = (body: OpenWfInfoBody) => trackPromise(axios.post(`${WORKFLOW}/openWfInfo`, body), 'modal');

/**
 * This API opens the document convalidate process
 * @param {number} protocol
 * @return {Promise<AxiosResponse<OpenConvalidaResponse>>}
 */
const openConvalidate = (protocol: number) =>
  trackPromise(axios.post<OpenConvalidaResponse>(`${WORKFLOW}/openConvalidateDocument`, { protocol }), 'modal');

/**
 * This API closes the convalidate process
 * @param {Object} body
 * @param {number} body.protocol
 * @return {Promise<AxiosResponse>}
 */
const closeConvalidateDocument = (body: CloseConvalidateDocumentBody) =>
  trackPromise(axios.post(`${WORKFLOW}/closeConvalidateDocument`, body), 'modal');

/**
 * Gets wf history table data
 * @param {number} protocol
 * @return {Promise<AxiosResponse<GetWfHistoryResponse[]>>}
 */
const getWfHistory = (protocol: number) =>
  trackPromise(axios.get<GetWfHistoryResponse[]>(`${WORKFLOW}/getWfHistory`, { params: { protocol } }), 'modal');

/**
 * This API post a note
 * @param {number} protocol
 * @param {number} idUser
 * @param {string} note
 * @return {Promise<AxiosResponse<void | SimpleError>>}
 */
const postWfHistoryNote = (protocol: number, idUser: number, note: string) =>
  trackPromise(
    axios.post<void | SimpleError>(`${WORKFLOW}/insertNoteIntoWfHistory`, { protocol, idUser, note }),
    'modal',
  );

/**
 * This API open the document reject previous process
 * @param {number} protocol
 * @return {Promise<AxiosResponse<OpenRejectToPrevious>>}
 */
const openRejectToPrevious = (protocol: number) =>
  trackPromise(axios.post<OpenRejectToPrevious>(`${WORKFLOW}/openRejectToPrevious?protocol=${protocol}`), 'modal');

/**
 * This API closes the document reject previous process
 * @param {Object} body
 * @param {number} body.protocol
 * @param {number} body.idPreviousTask
 * @param {string} body.note
 * @return {Promise<AxiosResponse>}
 */
const closeRejectToPrevious = (body: CloseRejectPrevious) =>
  trackPromise(axios.post(`${WORKFLOW}/closeRejectToPrevious`, body), 'modal');

/**
 * This API opens info from workflow
 * @param {Object} body
 * @param {number} body.company
 * @param {number} body.protocol
 * @return {Promise<AxiosResponse<OpenInfoFromWorkflow>>}
 */
const openInfoFromWorkflow = (body: { company: number; protocol: number }) =>
  trackPromise(axios.post<OpenInfoFromWorkflow>(`${WORKFLOW}/openInfoFromWorkflow`, body), 'modal');

/**
 * This API opens the document convalidate process for info
 * @param {number} protocol
 * @return {Promise<AxiosResponse<OpenConvalidaInfoResponse>>}
 */
const openConvalidateInfoDocument = (protocol: number) =>
  trackPromise(
    axios.post<OpenConvalidaInfoResponse>(`${WORKFLOW}/openConvalidateInfoDocument?protocol=${protocol}`),
    'modal',
  );

/**
 * This API closes the document convalidate process for info
 * @param {Object} body
 * @param {string} body.note
 * @param {number} body.protocol
 * @param {number} body.finalDestinationUser
 * @return {Promise<AxiosResponse>}
 */
const closeConvalidateInfoDocument = (body: CloseConvalidateInfoDocumentBody | null) =>
  trackPromise(axios.post(`${WORKFLOW}/closeConvalidateInfoDocument`, body), 'modal');

/**
 * This API is called to open the litigation process
 * @param {Object} body
 * @param {number} body.idCompany
 * @param {number[]} body.protocols
 * @return {Promise<AxiosResponse<LitigationCausalResponse>>}
 */
const openLitigation = (body: OpenLitigationBody) =>
  trackPromise(axios.post<LitigationCausalResponse>(`${WORKFLOW}/openLitigation`, body), 'modal');

/**
 * This API is called to close the litigation process
 * @param {Object} body
 * @param {number[]} body.protocols
 * @param {number} body.idCausal
 * @param {string} body.moveType
 * @param {boolean} body.mailAttach
 * @param {boolean} body.pdfAttach
 * @param {number} body.idCompany
 * @param {string} body.mailSupplier
 * @param {string} body.note
 * @param {string} body.mailCc
 * @return {Promise<AxiosResponse>}
 */
const closeLitigation = (body: CloseLitigationBody) =>
  trackPromise(axios.post(`${WORKFLOW}/closeLitigation`, body), 'modal');

/**
 * This API is called to print the document in the litigation process
 * @param {Object} body
 * @param {number[]} body.protocols
 * @param {number} body.idCausal
 * @param {number} body.idCompany
 * @param {string} body.text
 * @param {string} body.text2
 * @return {Promise<AxiosResponse>}
 */
const printPdfLitigationRequest = (body: PrintPdfLitigationRequest) =>
  trackPromise(axios.post(`${WORKFLOW}/printPdfLitigation`, body), 'modal');

/**
 * This API is called to open the CloseLitigation process
 * @param {number} protocol
 * @return {Promise<AxiosResponse<OpenCloseLitigationResponse>>}
 */
const openCloseLitigation = (protocol: number) =>
  trackPromise(
    axios.post<OpenCloseLitigationResponse>(`${WORKFLOW}/openCloseLitigation?protocol=${protocol}`),
    'modal',
  );

/**
 * This API is called to close the CloseLitigation process
 * @param {Object} body
 * @param {number[]} body.protocols
 * @param {string} body.note
 * @return {Promise<AxiosResponse>}
 */
const closeCloseLitigation = (body: { protocols: number[]; note: string }) =>
  trackPromise(
    axios.post<{ protocol: number; success: boolean; message: string }[]>(`${WORKFLOW}/closeCloseLitigation`, body),
    'modal',
  );

/**
 * This API is called to show the mail template
 * @param {idCompany} idCompany
 * @param {idCausal} idCausal
 * @param {protocol} protocol
 * @return {Promise<AxiosResponse>}
 */
const getLitigationMailTemplate = (idCompany: number, idCausal: number, protocol: number) =>
  trackPromise(axios.post(`${WORKFLOW}/getLitigationMailTemplate`, { idCompany, idCausal, protocol }), 'modal');

/**
 * This API closes the multiple convalidate process
 * @param {Object} body
 * @param {number[]} body.protocol
 * @param {string} body.note
 * @return {Promise<AxiosResponse<ApproveWfTaskReponse[]>>}
 */
const approveWfTask = (body: { protocols: number[]; note: string }) =>
  trackPromise(axios.post<ApproveWfTaskReponse[]>(`${WORKFLOW}/approveWfTask`, body), 'modal');

const closeParkLitigation = (body: { protocols: number[] }) =>
  trackPromise(axios.post(`${WORKFLOW}/closeParkLitigation`, body), 'module');

/**
 * Get workflow causal names for massive reject
 * @return {Promise<AxiosResponse<WfCausalNameReject[]>>}
 */
const getWfCausalNamesRejectForMassive = () =>
  trackPromise(axios.post<WfCausalNameReject[]>(`${WORKFLOW}/getWfCausalNamesRejectForMassive`), 'modal');

/**
 * Reject multiple documents
 * @param {Object} body
 * @param {number[]} body.protocols
 * @param {string} body.wfCausalName
 * @param {string} body.note
 * @return {Promise<AxiosResponse<string>>}
 */
const rejectWfTask = (body: { protocols: number[]; wfCausalName: string; note: string }) =>
  trackPromise(
    // eslint-disable-next-line camelcase
    axios.post<{ protocol: string; response: string; protocol_in: string; responseStatus: string }[]>(
      `${WORKFLOW}/rejectWfTask`,
      body,
    ),
    'modal',
  );

/**
 * Gets the current structure of the workflow
 * @param {number} protocol
 * @return {Promise<AxiosResponse<WfDefinitionEngine[]>>}
 */
const getCurrentStructureOfWorkflow = async (protocol: number) => {
  const response = await trackPromise(
    axios.get<WfDefinitionEngine>(`${WORKFLOW}/getCurrentStructureOfWorkflow`, { params: { protocol } }),
    'modal',
  );
  return response;
};

export default {
  getWfInfoForSuspend,
  closeReject,
  openReject,
  closeParkDocument,
  openParkDocument,
  openWorkFlow,
  getGroupsForInfo,
  openWfInfo,
  openConvalidate,
  closeConvalidateDocument,
  getWfHistory,
  postWfHistoryNote,
  openRejectToPrevious,
  closeRejectToPrevious,
  closeCloseParkDocument,
  openInfoFromWorkflow,
  openConvalidateInfoDocument,
  closeConvalidateInfoDocument,
  openLitigation,
  closeLitigation,
  printPdfLitigationRequest,
  openCloseLitigation,
  closeCloseLitigation,
  getLitigationMailTemplate,
  approveWfTask,
  closeParkLitigation,
  getWfCausalNamesRejectForMassive,
  rejectWfTask,
  getCurrentStructureOfWorkflow,
};
